import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { Logo, PageContainer } from "@/components/Logo";
import { MarketingFooter } from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => ({
    meta: [
      { title: "Terms of Service — MissionReady" },
      { name: "description", content: "The terms and conditions that govern use of MissionReady, our subscription field operations and patrol accountability platform." },
      ...socialMeta({
        title: "Terms of Service — MissionReady",
        description: "Terms governing use of the MissionReady subscription platform.",
        url: "https://missionready.dev/terms",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/terms" }],
    scripts: [...ORG_WEBSITE_JSONLD],
  }),
});

const LAST_UPDATED = "May 13, 2026";

function TermsPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur">
        <PageContainer className="flex h-16 items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm"><Link to="/login">Sign in</Link></Button>
            <Button asChild size="sm"><Link to="/signup">Start free trial</Link></Button>
          </div>
        </PageContainer>
      </header>

      <main>
        <PageContainer className="max-w-3xl py-16 md:py-24">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Legal</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight md:text-5xl">Terms of Service</h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

          <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/90">
            <section>
              <p>
                These Terms of Service ("Terms") govern your access to and use of the
                MissionReady website, web application, mobile clients, and APIs (the
                "Service"), provided by MissionReady ("MissionReady", "we", "us", or
                "our"). By creating an account or using the Service you agree to these
                Terms. If you are using the Service on behalf of an organization, you
                represent that you are authorized to bind it.
              </p>
            </section>

            <Section title="1. Accounts & workspaces">
              <p>
                You must provide accurate registration information and keep your credentials
                confidential. You are responsible for activity under your account. The first
                user of a workspace ("Customer") is its owner and may invite, remove, or
                change roles for additional users ("End Users").
              </p>
            </Section>

            <Section title="2. Subscription & trial">
              <ul className="list-disc space-y-2 pl-6">
                <li>The Service is offered on a subscription basis. Plans and limits are described on our pricing page.</li>
                <li>Free trials are limited as described at signup and convert to a paid plan only with your explicit confirmation.</li>
                <li>Subscriptions renew automatically for the same term unless cancelled before the renewal date.</li>
                <li>Fees are billed in advance, non-refundable except where required by law, and exclusive of taxes.</li>
                <li>We may change prices for future billing periods with at least 30 days' notice.</li>
              </ul>
            </Section>

            <Section title="3. Acceptable use">
              <p>You agree not to:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Use the Service to violate law, infringe rights, or harm others.</li>
                <li>Reverse engineer, scrape, or attempt to bypass security or rate limits.</li>
                <li>Upload malware or content you have no right to share.</li>
                <li>Resell, sublicense, or white-label the Service except under a plan that permits it.</li>
                <li>Use the Service for high-risk activities where failure could cause death, personal injury, or environmental damage.</li>
              </ul>
            </Section>

            <Section title="4. Customer data">
              <p>
                "Customer Data" means data you or your End Users submit to the Service. As
                between the parties, you retain all rights to Customer Data. You grant us a
                limited license to host, process, transmit, and display Customer Data solely
                to provide and improve the Service. You are responsible for the accuracy,
                legality, and right to use Customer Data, and for obtaining any consents
                required to track officer location.
              </p>
            </Section>

            <Section title="5. Privacy">
              <p>
                Our handling of personal data is described in our{" "}
                <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>,
                which is incorporated into these Terms.
              </p>
            </Section>

            <Section title="6. Service changes & beta features">
              <p>
                We continuously improve the Service and may add, modify, or discontinue
                features. Beta features are provided "as is" and may be changed or removed
                at any time.
              </p>
            </Section>

            <Section title="7. Third-party services">
              <p>
                The Service may integrate with third-party services (maps, payment, push
                notifications, AI providers). Your use of those services is subject to their
                terms; we are not responsible for them.
              </p>
            </Section>

            <Section title="8. Intellectual property">
              <p>
                MissionReady, the Service, and all associated software, designs, and content
                (excluding Customer Data) are owned by us or our licensors and protected by
                intellectual property laws. We grant you a limited, non-exclusive,
                non-transferable right to use the Service during your subscription.
              </p>
            </Section>

            <Section title="9. Suspension & termination">
              <p>
                We may suspend or terminate your access if you breach these Terms, fail to
                pay fees when due, or use the Service in a way that risks harm to others or
                to the Service. You may cancel at any time from billing settings;
                cancellation stops future renewals but does not refund fees already paid.
                On termination, you may export Customer Data for a reasonable period, after
                which it may be deleted.
              </p>
            </Section>

            <Section title="10. Disclaimers">
              <p>
                The Service is provided "as is" and "as available". To the maximum extent
                permitted by law, we disclaim all warranties, express or implied, including
                merchantability, fitness for a particular purpose, and non-infringement. We
                do not warrant that the Service will be uninterrupted, error-free, or that
                location, alerts, or analytics will be complete or accurate.
              </p>
            </Section>

            <Section title="11. Limitation of liability">
              <p>
                To the maximum extent permitted by law, MissionReady will not be liable for
                any indirect, incidental, special, consequential, or punitive damages, or
                any loss of profits, revenue, data, or goodwill. Our total aggregate
                liability arising out of or related to these Terms will not exceed the
                amount you paid us for the Service in the 12 months preceding the claim.
              </p>
            </Section>

            <Section title="12. Indemnification">
              <p>
                You will defend and indemnify MissionReady against claims, damages, and
                costs arising from your Customer Data, your use of the Service in violation
                of these Terms, or your violation of law or third-party rights.
              </p>
            </Section>

            <Section title="13. Governing law & disputes">
              <p>
                These Terms are governed by the laws of the jurisdiction in which
                MissionReady is established, excluding conflict-of-law rules. The parties
                submit to the exclusive jurisdiction of the courts of that jurisdiction,
                except that either party may seek injunctive relief in any competent court.
              </p>
            </Section>

            <Section title="14. Changes to these Terms">
              <p>
                We may update these Terms from time to time. Material changes will be
                announced in-product or by email at least 30 days before they take effect.
                Continued use of the Service after the effective date constitutes acceptance.
              </p>
            </Section>

            <Section title="15. Contact">
              <p>
                Questions about these Terms? Email{" "}
                <a className="text-primary hover:underline" href="mailto:legal@missionready.dev">legal@missionready.dev</a>.
              </p>
            </Section>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-border pt-8 text-sm">
            <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
            <span className="text-muted-foreground">·</span>
            <Link to="/" className="text-muted-foreground hover:text-foreground">Back to home</Link>
          </div>
        </PageContainer>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}
